import { Alert, Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { enablePush, getPushState, type PushState } from '../lib/push.js';

/**
 * Progressiver Benachrichtigungs-Onboarding-Hinweis. Zeigt beim Öffnen je nach Zustand
 * den NÄCHSTEN Schritt:
 *  - iPhone, noch nicht als App installiert → „Zum Home-Bildschirm hinzufügen" (iOS-Pflicht).
 *  - installiert / Android / Desktop, aber Push noch nicht an → „Aktivieren"-Button.
 *  - blockiert → Hinweis, es in den Einstellungen zu erlauben.
 *  - fertig → nichts.
 */
export function PushOnboarding(): JSX.Element | null {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const refresh = (): void => {
    getPushState()
      .then(setState)
      .catch(() => setState(null));
  };
  useEffect(() => {
    refresh();
  }, []);

  if (!state || hidden) return null;
  // Schon eingerichtet → kein Hinweis.
  if (state.subscribed && state.permission === 'granted') return null;

  const enable = async (): Promise<void> => {
    setBusy(true);
    try {
      await enablePush();
      refresh();
      notifications.show({ message: '🔔 Benachrichtigungen aktiviert', color: 'green' });
    } catch (e) {
      notifications.show({ message: String(e), color: 'red' });
    } finally {
      setBusy(false);
    }
  };

  // iPhone, noch nicht als App am Home-Bildschirm → das ist auf iOS Voraussetzung für Push.
  if (state.ios && !state.standalone) {
    return (
      <Alert
        color="violet"
        variant="light"
        radius="md"
        withCloseButton
        onClose={() => setHidden(true)}
        title="📲 Benachrichtigungen einrichten (Schritt 1/2)"
      >
        <Text size="sm">
          Tippe unten auf <b>Teilen</b> ⬆️ → <b>„Zum Home-Bildschirm"</b>. Danach betongold über
          das <b>neue Icon</b> öffnen — dann geht Schritt 2 (Benachrichtigungen erlauben).
        </Text>
      </Alert>
    );
  }

  if (!state.supported) return null;

  if (state.permission === 'denied') {
    return (
      <Alert
        color="orange"
        variant="light"
        radius="md"
        withCloseButton
        onClose={() => setHidden(true)}
        title="🔔 Benachrichtigungen sind blockiert"
      >
        <Text size="sm">
          Bitte in den {state.ios ? 'iPhone' : 'Browser'}-Einstellungen für betongold wieder
          erlauben, dann erneut versuchen.
        </Text>
      </Alert>
    );
  }

  // Installiert / Android / Desktop → direkt aktivierbar.
  return (
    <Alert
      color="violet"
      variant="light"
      radius="md"
      withCloseButton
      onClose={() => setHidden(true)}
      title={state.ios ? '🔔 Benachrichtigungen einrichten (Schritt 2/2)' : '🔔 Benachrichtigungen'}
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="sm" style={{ flex: 1, minWidth: 200 }}>
          Neue Top-Treffer sofort aufs Handy bekommen?
        </Text>
        <Button size="xs" onClick={enable} loading={busy}>
          Aktivieren
        </Button>
      </Group>
    </Alert>
  );
}
